import type { TabsProps } from "antd";
import { Alert, Button, Skeleton, Tabs } from "antd";
import { BarChart3, CreditCard, Handshake, Link2, Network } from "lucide-react";
import { useEffect, useState } from "react";

import Modal from "@/components/Modal";

import {
  AgentBankTab,
  AgentBasicTab,
  AgentBusinessTab,
  AgentCooperationTab,
  AgentHeaderCard,
  AgentInfoTab,
} from "./detailTabs";
import { useAgentDetail } from "./hooks/useAgentDetail";

/** 代理详情 Modal props */
export interface AgentDetailModalProps {
  open: boolean;
  onClose: () => void;
  /** 代理 ID，打开时拉取详情 */
  agentId: string | null;
  /** 打开关联账号详情 */
  onOpenAccount: (accountId: string) => void;
  /** 打开关联主体详情 */
  onOpenEntity: (entityId: string) => void;
  /** 打开下辖门店列表 */
  onOpenStoreList: (id: string) => void;
}

/** 代理详情 Modal：信息名片 + 四个 Tab */
export const AgentDetailModal = ({
  open,
  onClose,
  agentId,
  onOpenAccount,
  onOpenEntity,
  onOpenStoreList,
}: AgentDetailModalProps) => {
  /** 当前 Tab */
  const [activeTab, setActiveTab] = useState("basic");
  // const [isEditMode, setIsEditMode] = useState(false);
  /** 编辑模式（功能暂时关闭） */
  const isEditMode = false;
  const { agent, loading } = useAgentDetail(agentId, open);

  /** 关闭时重置 Tab */
  useEffect(() => {
    if (open) return;
    setActiveTab("basic");
  }, [open]);

  const tabItems: TabsProps["items"] = agent
    ? [
        {
          key: "basic",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <Network className="w-3.5 h-3.5" />
              基本信息
            </span>
          ),
          children: (
            <AgentBasicTab isEditMode={isEditMode} agent={agent} onOpenAccount={onOpenAccount} />
          ),
        },
        {
          key: "business",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5" />
              经营信息
            </span>
          ),
          children: (
            <AgentBusinessTab
              isEditMode={isEditMode}
              agent={agent}
              onOpenStoreList={onOpenStoreList}
            />
          ),
        },
        {
          key: "cooperation",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <Handshake className="w-3.5 h-3.5" />
              合作与其他
            </span>
          ),
          children: <AgentCooperationTab agent={agent} />,
        },
        {
          key: "bank",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <CreditCard className="w-3.5 h-3.5" />
              银行与开票
            </span>
          ),
          children: <AgentBankTab agent={agent} />,
        },
        {
          key: "info",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <Link2 className="w-3.5 h-3.5" />
              关联信息
            </span>
          ),
          children: (
            <AgentInfoTab
              isEditMode={isEditMode}
              agent={agent}
              onOpenAccount={onOpenAccount}
              onOpenEntity={onOpenEntity}
            />
          ),
        },
      ]
    : [];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={700}
      title={isEditMode ? "编辑代理详情" : "代理详情"}
      titleIcon={<Network className="w-5 h-5 text-orange-500" />}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>关闭</Button>
          {/* 编辑功能暂时注释
          {!isEditMode ? (
            <Button
              type="primary"
              icon={<Pencil className="w-3.5 h-3.5" />}
              disabled={!agent}
              onClick={() => setIsEditMode(true)}
            >
              编辑
            </Button>
          ) : (
            <Button type="primary" disabled={!agent} onClick={() => setIsEditMode(false)}>
              取消编辑
            </Button>
          )}
          */}
        </div>
      }
      styles={{ content: { height: "80vh" } }}
    >
      {loading ? (
        <div className="space-y-4">
          <Skeleton active avatar paragraph={{ rows: 2 }} />
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      ) : agent ? (
        <>
          <AgentHeaderCard agent={agent} />
          {isEditMode && <Alert className="mt-2!" message="双击字段可进入编辑状态" type="info" />}
          <Tabs className="mt-2" activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
        </>
      ) : (
        <div className="py-12 text-center text-sm text-slate-400">暂无代理详情</div>
      )}
    </Modal>
  );
};
